import { supabase } from '../lib/supabase';

export type QuoteStatus = 'DRAFT' | 'OPEN' | 'APPROVED' | 'CANCELLED';
export type QuoteLineType = 'LICENSE' | 'IMPLEMENTATION';

export interface Quote {
  id: string;
  reference_code: string;
  customer_id?: string;
  customer_name: string;
  project_name: string;
  status: QuoteStatus;
  quote_date: string;
  valid_until: string;
  notes?: string;
  licenses_included: boolean;
  implementation_included: boolean;
  on_demand_rate: number;
  project_mgmt_rate: number;
  licenses_monthly_total: number;
  licenses_annual_total: number;
  implementation_total: number;
  total_ex_vat: number;
  created_at?: string;
  updated_at?: string;
}

export interface QuoteLine {
  id: string;
  quote_id: string;
  line_type: QuoteLineType;
  group_name?: string;
  product_id?: string;
  product_name: string;
  // License fields
  unit_price?: number;
  quantity?: number;
  monthly_amount?: number;
  annual_amount?: number;
  // Implementation fields
  hourly_rate?: number;
  hours?: number;
  total_amount?: number;
  display_order: number;
  created_at?: string;
  updated_at?: string;
}

export interface QuoteInsert {
  reference_code: string;
  customer_id?: string;
  customer_name: string;
  project_name: string;
  status?: QuoteStatus;
  quote_date: string;
  valid_until: string;
  notes?: string;
  licenses_included?: boolean;
  implementation_included?: boolean;
  on_demand_rate?: number;
  project_mgmt_rate?: number;
}

export interface QuoteLineInsert {
  quote_id: string;
  line_type: QuoteLineType;
  group_name?: string;
  product_id?: string;
  product_name: string;
  unit_price?: number;
  quantity?: number;
  monthly_amount?: number;
  annual_amount?: number;
  hourly_rate?: number;
  hours?: number;
  total_amount?: number;
  display_order?: number;
}

export interface QuoteWithLines {
  quote: Quote;
  lines: QuoteLine[];
}

export const quoteService = {
  async getAll(): Promise<Quote[]> {
    // Fetch quotes with customer company name and latest user who modified it
    const { data, error } = await supabase
      .from('quotes')
      .select(`
        *,
        customers!quotes_customer_id_fkey (
          company
        ),
        quote_activity_audit (
          user_name,
          action_timestamp
        )
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    // Transform data to include company name and latest user
    const transformedData = (data || []).map((quote: any) => {
      // Get the most recent activity user
      const sortedActivities = (quote.quote_activity_audit || []).sort(
        (a: any, b: any) => new Date(b.action_timestamp).getTime() - new Date(a.action_timestamp).getTime()
      );
      const latestUser = sortedActivities[0]?.user_name || 'Unknown User';
      
      return {
        ...quote,
        company_name: quote.customers?.company || quote.customer_name,
        latest_user: latestUser,
      };
    });
    
    return transformedData;
  },

  async getById(id: string): Promise<QuoteWithLines | null> {
    const { data: quote, error: quoteError } = await supabase
      .from('quotes')
      .select('*')
      .eq('id', id)
      .single();

    if (quoteError) throw quoteError;
    if (!quote) return null;

    const { data: lines, error: linesError } = await supabase
      .from('quote_lines')
      .select('*')
      .eq('quote_id', id)
      .order('display_order');

    if (linesError) throw linesError;

    return {
      quote,
      lines: lines || [],
    };
  },

  async getByIdWithCustomer(id: string): Promise<{ quote: Quote; lines: QuoteLine[]; customer: any } | null> {
    const { data: quote, error: quoteError } = await supabase
      .from('quotes')
      .select('*')
      .eq('id', id)
      .single();

    if (quoteError) throw quoteError;
    if (!quote) return null;

    const { data: lines, error: linesError } = await supabase
      .from('quote_lines')
      .select('*')
      .eq('quote_id', id)
      .order('display_order');

    if (linesError) throw linesError;

    let customer = null;
    if (quote.customer_id) {
      const { data: customerData, error: customerError } = await supabase
        .from('customers')
        .select('*')
        .eq('id', quote.customer_id)
        .single();
      
      if (!customerError && customerData) {
        customer = customerData;
      }
    }

    return {
      quote,
      lines: lines || [],
      customer,
    };
  },

  async create(quoteData: QuoteInsert): Promise<Quote> {
    const { data, error } = await supabase
      .from('quotes')
      .insert(quoteData)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, quote: QuoteInsert): Promise<Quote> {
    const { data, error } = await supabase
      .from('quotes')
      .update({
        reference_code: quote.reference_code,
        customer_id: quote.customer_id,  // ✅ ADD THIS LINE - Fix customer_id update
        customer_name: quote.customer_name,
        project_name: quote.project_name,
        status: quote.status,
        quote_date: quote.quote_date,
        valid_until: quote.valid_until,
        notes: quote.notes,
        licenses_included: quote.licenses_included,
        implementation_included: quote.implementation_included,
        on_demand_rate: quote.on_demand_rate,
        project_mgmt_rate: quote.project_mgmt_rate,
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('quotes')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async addLine(line: QuoteLineInsert): Promise<QuoteLine> {
    const { data, error } = await supabase
      .from('quote_lines')
      .insert({
        quote_id: line.quote_id,
        line_type: line.line_type,
        group_name: line.group_name,
        product_id: line.product_id,
        product_name: line.product_name,
        unit_price: line.unit_price,
        quantity: line.quantity,
        monthly_amount: line.monthly_amount,
        annual_amount: line.annual_amount,
        hourly_rate: line.hourly_rate,
        hours: line.hours,
        total_amount: line.total_amount,
        display_order: line.display_order,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateLine(lineId: string, line: QuoteLineInsert): Promise<QuoteLine> {
    const { data, error } = await supabase
      .from('quote_lines')
      .update({
        group_name: line.group_name,
        product_id: line.product_id,
        product_name: line.product_name,
        unit_price: line.unit_price,
        quantity: line.quantity,
        monthly_amount: line.monthly_amount,
        annual_amount: line.annual_amount,
        hourly_rate: line.hourly_rate,
        hours: line.hours,
        total_amount: line.total_amount,
        display_order: line.display_order,
      })
      .eq('id', lineId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteLine(lineId: string): Promise<void> {
    const { error } = await supabase
      .from('quote_lines')
      .delete()
      .eq('id', lineId);

    if (error) throw error;
  },

  async getLinesByQuoteId(quoteId: string): Promise<QuoteLine[]> {
    const { data, error } = await supabase
      .from('quote_lines')
      .select('*')
      .eq('quote_id', quoteId)
      .order('display_order');

    if (error) throw error;
    return data || [];
  },

  async duplicateQuote(quoteId: string, newReferenceCode: string): Promise<Quote> {
    const original = await this.getById(quoteId);
    if (!original) throw new Error('Quote not found');

    const { quote, lines } = original;

    const newQuote = await this.create({
      reference_code: newReferenceCode,
      customer_id: quote.customer_id,
      customer_name: quote.customer_name,
      project_name: `${quote.project_name} (Copy)`,
      status: 'DRAFT',
      quote_date: new Date().toISOString().split('T')[0],
      valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      notes: quote.notes,
      licenses_included: quote.licenses_included,
      implementation_included: quote.implementation_included,
      on_demand_rate: quote.on_demand_rate,
      project_mgmt_rate: quote.project_mgmt_rate,
    });

    for (const line of lines) {
      await this.addLine({
        quote_id: newQuote.id,
        line_type: line.line_type,
        group_name: line.group_name,
        product_id: line.product_id,
        product_name: line.product_name,
        unit_price: line.unit_price,
        quantity: line.quantity,
        monthly_amount: line.monthly_amount,
        annual_amount: line.annual_amount,
        hourly_rate: line.hourly_rate,
        hours: line.hours,
        total_amount: line.total_amount,
        display_order: line.display_order,
      });
    }

    return newQuote;
  },

  async updateStatus(id: string, status: QuoteStatus): Promise<Quote> {
    return this.update(id, { status });
  },

  async filterByStatus(status: QuoteStatus): Promise<Quote[]> {
    const { data, error } = await supabase
      .from('quotes')
      .select('*')
      .eq('status', status)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async searchQuotes(searchTerm: string): Promise<Quote[]> {
    const { data, error } = await supabase
      .from('quotes')
      .select('*')
      .or(`reference_code.ilike.%${searchTerm}%,customer_name.ilike.%${searchTerm}%,project_name.ilike.%${searchTerm}%`)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },
};